import { api } from '@/lib/trpc/react';
import { md5 } from '../../utils';
import type { CitationAttributes } from './citation-extension';

type CitationFormat =
  | 'APA_7'
  | 'MLA_9'
  | 'HARVARD'
  | 'CHICAGO'
  | 'IEEE'
  | 'CSE'
  | 'ACS'
  | 'VANCOUVER'
  | 'ASA'
  | 'AMA';

const CitationItem = ({
  citation,
  format,
}: {
  citation: CitationAttributes['citation'];
  format: CitationFormat;
}) => {
  const citationId = md5(citation.url);

  const {
    data: citationData,
    isLoading,
    isError,
  } = api.citations.format.useQuery({
    format,
    citation,
  });

  if (isLoading) {
    return (
      <li className="text-textGray4 animate-pulse">Loading citation...</li>
    );
  }

  if (isError) {
    return (
      <li className="text-textGray4">
        Error loading citation:{' '}
        {(isError as unknown as Error).message || 'Unknown error'}
      </li>
    );
  }
  // Check if citationData is a string (direct citation)
  if (typeof citationData === 'string') {
    return (
      <li id={citationId} className="citation-item">
        <span
          className="prose text-foreground prose-a:text-foreground prose-a:whitespace-pre-wrap break-words"
          // biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation>
          dangerouslySetInnerHTML={{ __html: makeUrlsClickable(citationData) }}
        />
      </li>
    );
  }

  // If we reach here, we couldn't find a valid citation format
  console.error('Unexpected citation data structure:', citationData);
  return (
    <li id={citationId} className="text-textGray4">
      <span className="italic">Citation cannot be found</span>
    </li>
  );
};

export const makeUrlsClickable = (text: string) => {
  const urlRegex = /<?(https?:\/\/[^\s>]+)>?\.{3}?/g;
  return text.replace(urlRegex, (_match, url) => {
    const cleanUrl = url.replace(/^<|>$/g, '');
    return `<a href="${cleanUrl}" class="underline" target="_blank" rel="noreferrer">${cleanUrl}</a>`;
  });
};

export default CitationItem;
